import React from "react";

import { flattenChildren } from "./flattenChildren";
import { buildSlots, isChildOfType, SlotConfig, SlotValue } from "./slotUtils";

type TypedChildren<Config extends SlotConfig> = {
  [Property in keyof Config]: Array<SlotValue<Config, Property>>;
};

export function getTypedChildren<Config extends SlotConfig>(
  children: React.ReactNode,
  config: Config
): [TypedChildren<Config>, React.ReactNode[]] {
  const typedChildren: TypedChildren<Config> = buildSlots(config, () => []);
  const rest: React.ReactNode[] = [];

  const keys = Object.keys(config) as Array<keyof Config>;
  const values = Object.values(config);

  flattenChildren(children).forEach((child) => {
    const index = values.findIndex((value) => isChildOfType(child, value));

    // If the child is not a slot, add it to the `rest` array
    if (index === -1) {
      rest.push(child);
      return;
    }

    const slotKey = keys[index] as keyof Config;

    // If the child is a slot, add it to the `slots` object
    typedChildren[slotKey]?.push(child as SlotValue<Config, keyof Config>);
  });

  return [typedChildren, rest.filter(Boolean)];
}
